import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة معدل شرب القهوة اليومي - حساب كمية الكافيين الآمنة";
const DESCRIPTION =
  "استخدم حاسبة معدل شرب القهوة اليومي لمعرفة كمية الكافيين المناسبة لك بناءً على وزنك ونوع القهوة. احسب الجرعة الآمنة وتجنب مخاطر زيادة الكافيين.";
const KEYWORDS =
  "حاسبة القهوة, كمية الكافيين اليومية, حساب الكافيين في القهوة, معدل شرب القهوة, اضرار الكافيين, فوائد القهوة, caffeine calculator, قهوة سعودية, اسبريسو";
const OG_URL = "https://do-calculate.com/calculator/ar/coffee-caffeine/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة معدل شرب القهوة اليومي",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "HealthApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات الصحية",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة معدل شرب القهوة",
        item: OG_URL,
      },
    ],
  },
];

// Caffeine content per unit (mg) based on common sources (SFDA & OmniCalculator)
const BEVERAGES = {
  espresso: { name: "اسبريسو (30 مل)", caffeine: 63, unit: "فنجان" },
  latte: { name: "لاتيه / كابتشينو (240 مل)", caffeine: 77, unit: "كوب" },
  brewed: { name: "قهوة مقطرة / أمريكية (240 مل)", caffeine: 96, unit: "كوب" },
  instant: { name: "قهوة سريعة التحضير (240 مل)", caffeine: 62, unit: "كوب" },
  turkish: { name: "قهوة تركية (60 مل)", caffeine: 50, unit: "فنجان" },
  saudi: { name: "قهوة سعودية (فنجان صغير)", caffeine: 15, unit: "فنجان" },
  black_tea: { name: "شاي أسود (240 مل)", caffeine: 47, unit: "كوب" },
  green_tea: { name: "شاي أخضر (240 مل)", caffeine: 28, unit: "كوب" },
  energy_drink: { name: "مشروب طاقة (250 مل)", caffeine: 80, unit: "علبة" },
  cola: { name: "مشروب غازي كولا (355 مل)", caffeine: 34, unit: "علبة" },
};

function CoffeeCaffeineCalculator() {
  const [weight, setWeight] = useState("");
  const [beverageType, setBeverageType] = useState("espresso");
  const [dailyCups, setDailyCups] = useState(1);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateCaffeine = () => {
    try {
      setError("");
      const w = parseFloat(weight);
      const cups = parseFloat(dailyCups);

      if (!w || w <= 0) {
        setResult(null);
        return;
      }

      // Standard healthy limit for adults is 400mg per day (SFDA)
      // Toxic limit is roughly 150mg per kg of body weight
      // Dangerous dose starts at around 3mg to 6mg per kg for sensitivity
      const safeLimit = 400; 
      const selectedBeverage = BEVERAGES[beverageType];
      const caffeinePerCup = selectedBeverage.caffeine;
      const totalConsumed = caffeinePerCup * cups;

      const cupsToLimit = Math.floor(safeLimit / caffeinePerCup);
      const lethalDoseMg = w * 150; // Approximated lethal dose
      const cupsToLethal = Math.floor(lethalDoseMg / caffeinePerCup);

      let status = "";
      let statusColor = "";
      if (totalConsumed <= 200) {
        status = "استهلاك آمن ومنخفض";
        statusColor = "#1a5f3f";
      } else if (totalConsumed <= 400) {
        status = "استهلاك معتدل (ضمن الحدود الموصى بها)";
        statusColor = "#856404";
      } else {
        status = "استهلاك مرتفع (تجاوزت الحد اليومي الآمن)";
        statusColor = "#721c24";
      }

      setResult({
        totalConsumed,
        safeLimit,
        cupsToLimit,
        cupsToLethal,
        status,
        statusColor,
        beverageName: selectedBeverage.name,
        unit: selectedBeverage.unit,
      });
    } catch (err) {
      setError("حدث خطأ أثناء الحساب. يرجى التأكد من البيانات.");
    }
  };

  useEffect(() => {
    calculateCaffeine();
  }, [weight, beverageType, dailyCups]);

  const handleClear = () => {
    setWeight("");
    setBeverageType("espresso");
    setDailyCups(1);
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة معدل شرب القهوة اليومي</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                تساعدك <strong>حاسبة معدل شرب القهوة</strong> على تتبع كمية الكافيين التي تستهلكها يومياً. 
                توصي هيئة الغذاء والدواء السعودية (SFDA) بأن لا يتجاوز استهلاك البالغين الأصحاء من الكافيين 
                <strong> 400 ملجم </strong> يومياً. أدخل وزنك ونوع مشروبك المفضل لمعرفة حدودك الآمنة.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>بيانات الاستهلاك</h2>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label >وزن الجسم (كجم)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="مثال: 70"
                  />
                </div>
                <div className="col-md-6">
                  <label >نوع المشروب</label>
                  <select
                    className="form-select"
                    value={beverageType}
                    onChange={(e) => setBeverageType(e.target.value)}
                  >
                    {Object.entries(BEVERAGES).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label >عدد الأكواب/الفناجين يومياً</label>
                  <input
                    type="number"
                    className="form-control"
                    value={dailyCups}
                    onChange={(e) => setDailyCups(e.target.value)}
                    min="1"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  مسح البيانات
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>النتائج:</strong>
                  </h3>
                  <table className="table bmi-table" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th className="bmi-header" style={{ textAlign: "center" }}>البيان</th>
                        <th className="bmi-header" style={{ textAlign: "center" }}>القيمة</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: "center" }}>إجمالي الكافيين المستهلك</td>
                        <td style={{ textAlign: "center", fontWeight: "bold", color: result.statusColor }}>
                          {result.totalConsumed} ملجم
                        </td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "center" }}>الحالة</td>
                        <td style={{ textAlign: "center", fontWeight: "bold", color: result.statusColor }}>
                          {result.status}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "center" }}>الحد الأقصى الآمن يومياً</td>
                        <td style={{ textAlign: "center" }}>400 ملجم</td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "center" }}>عدد الـ {result.unit} المسموحة لتجنب الأعراض</td>
                        <td style={{ textAlign: "center", fontWeight: "bold" }}>
                          {result.cupsToLimit} {result.unit}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="info-sections-container mt-4">
                    <div className="info-section">
                      <div className="info-section-header"><strong>تحذير الجرعة الخطرة</strong></div>
                      <div className="info-section-body">
                        <p>بناءً على وزنك ({weight} كجم)، قد تصبح كمية الكافيين خطيرة جداً أو قاتلة إذا وصلت إلى استهلاك:</p>
                        <h4 className="text-danger text-center">{result.cupsToLethal} {result.unit}</h4>
                        <p className="small text-muted text-center">* هذا الرقم تقريبي للأغراض التعليمية فقط، الحساسية للكافيين تختلف من شخص لآخر.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <hr />
            <RelatedLinks />
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>دليل استهلاك الكافيين الآمن</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  الكافيين مادة منبهة طبيعية توجد في القهوة والشاي والشوكولاتة. بينما توفر اليقظة والنشاط، فإن الإفراط فيها يؤدي لآثار جانبية.
                </p>
                <h3 style={{ fontSize: "17px" }}>الحدود الموصى بها (حسب هيئة الغذاء والدواء السعودية):</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>البالغون الأصحاء:</strong> 400 ملجم كحد أقصى (ما يعادل 4 أكواب من القهوة المقطرة).</li>
                  <li><strong>الحوامل والمرضعات:</strong> يجب ألا يتجاوز الاستهلاك 200 ملجم يومياً.</li>
                  <li><strong>الأطفال والمراهقون:</strong> ينصح بتقليل الاستهلاك لأدنى المستويات (لا يتجاوز 2.5 ملجم لكل كجم من وزن الجسم).</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>أعراض زيادة جرعة الكافيين</strong>
              </div>
              <div className="info-section-body">
                <p>إذا تجاوزت الحد المسموح، قد تشعر بالأعراض التالية:</p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>الأرق وصعوبة النوم.</li>
                  <li>تسارع ضربات القلب.</li>
                  <li>القلق والتوتر العصبي.</li>
                  <li>اضطرابات المعدة.</li>
                  <li>الصداع والرعشة.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>محتوى الكافيين في المشروبات الشائعة</strong>
              </div>
              <div className="info-section-body">
                <table className="table bmi-table">
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>المشروب</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>كمية الكافيين (تقريباً)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ textAlign: "center" }}>فنجان قهوة سعودية</td><td style={{ textAlign: "center" }}>15 ملجم</td></tr>
                    <tr><td style={{ textAlign: "center" }}>كوب قهوة أمريكية (240 مل)</td><td style={{ textAlign: "center" }}>95-165 ملجم</td></tr>
                    <tr><td style={{ textAlign: "center" }}>شوت اسبريسو (30 مل)</td><td style={{ textAlign: "center" }}>63 ملجم</td></tr>
                    <tr><td style={{ textAlign: "center" }}>كوب شاي أسود</td><td style={{ textAlign: "center" }}>47 ملجم</td></tr>
                    <tr><td style={{ textAlign: "center" }}>علبة مشروب طاقة</td><td style={{ textAlign: "center" }}>80 ملجم</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoffeeCaffeineCalculator;